import { CREDIT_PACKS, PLAN_LIMITS, planPriceCents } from "@amtana/billing-contract";
import type { OfferedPlan } from "@amtana/billing-contract";
import { WELCOME_CREDITS } from "@amtana/growth-contract";

import { WEBSITE_PLANS } from "./website-plans.ts";

// The cheapest true way to reach a number of credits (D1339, S5.22). The helper asks for credits a
// month, so a way is measured by what one month of it costs. A pack needs no plan, is paid once
// and carries over until used (ADR-019), so packs compete with a plan and may be bought repeatedly.
// Welcome credits are never added to a paid way: they come once, and not to everyone.

type Pack = { readonly credits: number; readonly cents: number };

export type CreditPrices = {
  readonly welcome: number;
  /** In the order to prefer on a tie, each at its share of one month on the chosen period. */
  readonly plans: readonly {
    readonly plan: OfferedPlan;
    readonly credits: number;
    readonly monthCents: number;
  }[];
  readonly packs: readonly Pack[];
};

export type CreditWay =
  | { readonly kind: "welcome" }
  | {
      readonly kind: "paid";
      /** Undefined when packs alone are cheapest: the person stays on the free plan. */
      readonly plan: OfferedPlan | undefined;
      readonly packs: number;
      readonly packCents: number;
    };

/** The contract's numbers on a billing period, for the plans the website shows (D1378). */
export function pricesFor(period: "month" | "year"): CreditPrices {
  return {
    welcome: WELCOME_CREDITS,
    plans: WEBSITE_PLANS.map((plan) => ({
      plan,
      credits: PLAN_LIMITS[plan].credits ?? 0,
      monthCents: planPriceCents(plan, period) / (period === "year" ? 12 : 1),
    })),
    packs: CREDIT_PACKS.analysis,
  };
}

const greatestDivisor = (a: number, b: number): number => (b === 0 ? a : greatestDivisor(b, a % b));

const cheaperPerCredit = (best: Pack, pack: Pack): Pack =>
  pack.cents * best.credits < best.cents * pack.credits ? pack : best;

/**
 * The fewest cents that buy at least `credits`, packs repeating as needed. Any `best.credits` other
 * packs hold a group adding to a multiple of the pack cheapest per credit, and swapping that group
 * never costs more. So all but a bounded remainder is that pack, and the table stays small however
 * large the number.
 */
export function cheapestPacks(
  credits: number,
  packs: readonly Pack[],
): { readonly count: number; readonly cents: number } {
  const [first] = packs;
  if (credits <= 0 || !first) return { count: 0, cents: 0 };
  const step = packs.reduce((divisor, pack) => greatestDivisor(divisor, pack.credits), first.credits);
  const best = packs.reduce(cheaperPerCredit, first);
  const largest = Math.max(...packs.map((pack) => pack.credits));
  const bound = (best.credits / step) * largest;
  const repeats = Math.max(0, Math.ceil((credits - bound) / best.credits));
  const steps = Math.ceil((credits - repeats * best.credits) / step);

  const table = [{ count: 0, cents: 0 }];
  for (let reached = 1; reached <= steps; reached += 1) {
    let cheapest = { count: Number.POSITIVE_INFINITY, cents: Number.POSITIVE_INFINITY };
    for (const pack of packs) {
      const before = table[Math.max(0, reached - pack.credits / step)] ?? { count: 0, cents: 0 };
      const cents = before.cents + pack.cents;
      const count = before.count + 1;
      if (cents < cheapest.cents || (cents === cheapest.cents && count < cheapest.count)) {
        cheapest = { count, cents };
      }
    }
    table[reached] = cheapest;
  }
  const rest = table[steps] ?? { count: 0, cents: 0 };
  return { count: rest.count + repeats, cents: rest.cents + repeats * best.cents };
}

/** What one month costs on each way, least first; on a tie a plan beats packs, a smaller plan a larger. */
export function cheapestWay(credits: number, prices: CreditPrices): CreditWay {
  if (credits <= prices.welcome) return { kind: "welcome" };
  const alone = cheapestPacks(credits, prices.packs);
  let way: CreditWay = { kind: "paid", plan: undefined, packs: alone.count, packCents: alone.cents };
  let lowest = alone.cents;
  for (const entry of prices.plans.toReversed()) {
    const extra = cheapestPacks(credits - entry.credits, prices.packs);
    const cost = entry.monthCents + extra.cents;
    if (cost <= lowest) {
      way = { kind: "paid", plan: entry.plan, packs: extra.count, packCents: extra.cents };
      lowest = cost;
    }
  }
  return way;
}
